import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { GET_PRODUCT_AVAILABILITY } from '../graphql/product-queries'
import { AVAILABILITY_FILTERS, AVAILABILITY_POLL_MS } from '../lib/availability'
import type { Product } from '../types/product'

/**
 * Every product's sold-out state, polled. Page, entry buttons and register share one
 * cache entry (same variables), and Product is normalized by id — so this poll also
 * refreshes the register tiles and the wizards' meal-deal parts, which read the same
 * Product entries.
 */
export const useProductAvailability = (skip = false) => {
  const { data, loading } = useQuery(GET_PRODUCT_AVAILABILITY, {
    variables: { filters: AVAILABILITY_FILTERS },
    pollInterval: AVAILABILITY_POLL_MS,
    fetchPolicy: 'cache-and-network',
    skip,
  })
  const products: Product[] = data?.products ?? []
  return { products, loading }
}

/**
 * The same polled product set, read from the cache only — no request and no second timer; the
 * register page keeps the entry fresh. Product id → live own-systems sold-out mark, for the
 * wizards' option items (an item follows its linked product; owner 2026-10-27: no option poll).
 */
export const useSoldOutProducts = (): Map<number, boolean> => {
  const { data } = useQuery(GET_PRODUCT_AVAILABILITY, {
    variables: { filters: AVAILABILITY_FILTERS },
    fetchPolicy: 'cache-only',
  })
  return useMemo(
    () => new Map<number, boolean>((data?.products ?? []).map((p: Product) => [Number(p.id), !!p.sold_out])),
    [data]
  )
}
